import React, { useEffect, useState } from "react";

import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { updateUserProfileAsync } from "../../../redux/features/adminSlice";
import { editAgentValidationSchema } from "./schema";

interface FormDataProps {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
}

interface EditAgentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedAgent: any;
}

const EditAgentModal: React.FC<EditAgentModalProps> = ({
  open,
  onClose,
  selectedAgent,
  onSave,
}) => {
  const dispatch = useAppDispatch();

  const [formValues, setFormValues] = useState<FormDataProps>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });

  const [errors, setErrors] = useState<Partial<FormDataProps>>({});
  const { isLoading } = useAppSelector((state: RootState) => state.admin);

  useEffect(() => {
    if (selectedAgent) {
      const [firstName, lastName] = selectedAgent.agentName.trim().split(" ");
      setFormValues({
        firstName: firstName ?? "",
        lastName: lastName ?? "",
        email: selectedAgent.agentEmail ?? "",
        phoneNumber: selectedAgent.agentPhoneNumber ?? "",
      });
    }
  }, [selectedAgent]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleValidation = async () => {
    try {
      await editAgentValidationSchema.validate(formValues, {
        abortEarly: false,
      });
      setErrors({});
      return true;
    } catch (err: any) {
      const newErrors: any = {};
      err.inner.forEach((error: any) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    const isValid = await handleValidation();
    if (isValid) {
      dispatch(
        updateUserProfileAsync({ id: selectedAgent.agentId, ...formValues })
      )
        .unwrap()
        .then(() => {
          toast.success("Agent updated successfully");
          onClose();
          onSave();
        })
        .catch(() => {
          toast.error("Failed to update Agent");
        });
    }
  };

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Agent"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={onClose}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Done"
            type="button"
            onClick={handleSubmit}
            layout="inline"
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-6">
        <ModalInput
          label="firstName"
          placeholder="Enter firstName"
          name="firstName"
          value={formValues.firstName}
          onChange={handleInputChange}
          error={errors.firstName}
          required
        />
        <ModalInput
          label="lastName"
          placeholder="Enter lastName"
          name="lastName"
          value={formValues.lastName}
          onChange={handleInputChange}
          error={errors.lastName}
          required
        />
        <ModalInput
          label="Email Address"
          type="email"
          placeholder="Enter email"
          name="email"
          value={formValues.email}
          onChange={handleInputChange}
          error={errors.email}
          required
        />
        <ModalInput
          label="Phone number"
          type="tel"
          placeholder="Enter phone number"
          name="phoneNumber"
          value={formValues.phoneNumber}
          onChange={handleInputChange}
          error={errors.phoneNumber}
          required
        />
        {/* Optionally, add dropdowns for roles and address here */}
      </div>
    </CustomModalContainer>
  );
};

export default EditAgentModal;
